import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthContextType } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  // Validate session on mount
  useEffect(() => {
    const validateSession = async () => {
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        setToken(storedToken);
        try {
          const response = await fetch(`${API_URL}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${storedToken}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            setIsAuthenticated(true);
          } else {
            // Token is invalid or expired
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Session validation error:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };

    validateSession();
  }, []);

  /**
   * Sign out on any 401 from our own API.
   *
   * Sessions used to live in an in-memory Map on the server, so every deploy invalidated them.
   * Since v1.19.2 they survive deploys, but still end after 30 idle days, on sign-out elsewhere
   * after a password change, or when an admin deactivates the account. Before
   * this, a signed-out user saw whatever error each page happened to render — "failed to get
   * token", an empty list, a silent nothing — and had to work out for themselves that reloading
   * would fix it. There are around 60 fetch sites; patching each one would miss some and drift,
   * so `fetch` is wrapped once here instead and every existing call is covered untouched.
   *
   * Deliberately narrow: only our own API, and never the auth endpoints. A 401 from
   * /auth/login is a wrong password and must surface as one, not as an expired session.
   */
  useEffect(() => {
    const original = window.fetch;

    window.fetch = async (input, init) => {
      const response = await original(input, init);

      if (response.status === 401) {
        const url =
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.toString()
              : input instanceof Request
                ? input.url
                : '';
        const isOurApi = url.includes('/api/');
        // A failed sign-in and a re-validated dead token are both 401s from these paths
        const isAuthCall = /\/auth\/(login|logout|me|forgot-password)/.test(url);

        if (isOurApi && !isAuthCall && localStorage.getItem('authToken')) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setUser(null);
          setIsAuthenticated(false);
          setToken(null);
          // The reason travels in the URL rather than localStorage. A dying session usually
          // produces several 401s at once — one page can have half a dozen requests in
          // flight — and a stored flag gets consumed by the first render and then rewritten
          // by the stragglers, so the notice showed up only sometimes. A query parameter is
          // set by whichever 401 lands first and read from the URL, which cannot race.
          //
          // Full reload rather than a router navigate: pages hold state fetched under the old
          // session, and unwinding all of it is more fragile than starting clean.
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login?expired=1';
          }
        }
      }

      return response;
    };

    return () => {
      window.fetch = original;
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
        setToken(data.token);
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        return true;
      } else {
        const error = await response.json();
        console.error('Login failed:', error.error);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      try {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${storedToken}`
          }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    setUser(null);
    setIsAuthenticated(false);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  };

  // Re-read the current user from the server. Called after a profile change so the
  // header avatar and name update without needing a sign-out.
  const refreshUser = async () => {
    const activeToken = token || localStorage.getItem('authToken');
    if (!activeToken) return;

    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${activeToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
    } catch (error) {
      // A failure here only means the chrome shows stale details; not worth surfacing
      console.error('Could not refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, isLoading, token, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
